"use client";

import { c as _c } from "react/compiler-runtime";
import { useMemo } from "react";
import { DynamicDocumentInstructions, WorkflowAction } from "@/configuration/document-mappings/types";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { useDocumentManagementInstructions } from "@/src/lib/data-fetchers/document-management-instructions-fetchers";
import { useFaxLocationAssignment } from "@/src/lib/data-fetchers/location-assignment-fetcher";
import { isPassthroughAssignee } from "@/src/lib/ehr/customer/heartplace/passthrough-assignee";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { renderTemplate } from "@/src/lib/templating";
import type { StepOutputs } from "@/src/lib/workflows/steps/step-outputs";
import { Section } from "@/src/models/section";
import { Task } from "@/src/models/task";
export interface InstructionField {
  label: string;
  value: string;
}
export interface ProcessedInstructions {
  displayName: string;
  standardFields: InstructionField[];
  extraFields: InstructionField[];
  instructions: readonly string[] | null;
  note: string | null;
  noteLabel: string | null;
  statusLine: string | null;
  workflowAction: WorkflowAction | null;
  additionalWorkflow: readonly string[] | null;
  faxRecipientNumber: string | null;
  tags: readonly string[] | null;
  stepOutputs: StepOutputs | null;
  ccDescription: string | null;
  processInstructionText: (text: string) => string;
}
interface LocationAssignment {
  authLocation?: string | null;
  staffLocation?: string | null;
  locationPrefix?: string | null;
  faxRecipientNumber?: string | null;
  resolvedAssignment?: string | null;
}
function getGroupExtraFields(groupName: string, patientFamilyName: string | null | undefined, patientGivenName: string | null | undefined, patientEhrSystemId: string | null, ehrDocumentId: string | null | undefined): InstructionField[] {
  switch (groupName) {
    case ConfigSubdomain.STRIDECARE:
      return [{
        label: "Patient Name",
        value: [patientFamilyName, patientGivenName].filter(Boolean).join(", ") || "N/A"
      }, {
        label: "EHR ID",
        value: patientEhrSystemId || "N/A"
      }, {
        label: "Document ID",
        value: ehrDocumentId || "N/A"
      }];
    default:
      return [];
  }
}
function replaceLocationPlaceholder(text: string | null, locationValue: string | null): string {
  if (!text) {
    return locationValue || "";
  }
  if (!locationValue) {
    return text;
  }
  return text.replace(/\{\{?location\}\}?/g, locationValue);
}
export function useBuildInstructions(task, section, mapping, stepOutputs, ccDescription, t0) {
  const $ = _c(77);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const location = t1;
  const {
    authLocation: t2,
    staffLocation: t3,
    locationPrefix: t4,
    faxRecipientNumber: t5,
    resolvedAssignment: t6
  } = location;
  const authLocation = t2 === undefined ? null : t2;
  const staffLocation = t3 === undefined ? null : t3;
  const locationPrefix = t4 === undefined ? null : t4;
  const faxRecipientNumber = t5 === undefined ? null : t5;
  const resolvedAssignment = t6 === undefined ? null : t6;
  const reviewedContent = section.reviewedContent;
  const patientFamilyName = task.patient?.familyName;
  const patientGivenName = task.patient?.givenName;
  let t7;
  if ($[2] !== task.patient) {
    t7 = task.patient ? tryGetEhrSystemId(task.patient) : null;
    $[2] = task.patient;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const patientEhrSystemId = t7;
  const ehrDocumentId = section.ehrDocumentId;
  let t8;
  bb0: {
    if (!mapping) {
      t8 = null;
      break bb0;
    }
    let isHeartplaceNonMrr;
    let processInstructionText;
    let t10;
    let t11;
    let t12;
    let t13;
    let t14;
    let t15;
    let t9;
    if ($[4] !== authLocation || $[5] !== mapping.displayName || $[6] !== mapping.documentLabel || $[7] !== mapping.folder || $[8] !== mapping.group || $[9] !== mapping.inputDate || $[10] !== mapping.priority || $[11] !== mapping.workflowType || $[12] !== reviewedContent) {
      const templateContent = reviewedContent || {};
      const group = {
        subdomain: mapping.group
      };
      processInstructionText = text => {
        const rendered = renderTemplate(text, templateContent, group);
        return replaceLocationPlaceholder(rendered, authLocation);
      };
      isHeartplaceNonMrr = mapping.group === ConfigSubdomain.HEARTPLACE;
      t11 = !isHeartplaceNonMrr ? [{
        label: "Workflow Type",
        value: renderTemplate(mapping.workflowType, templateContent, group)
      }] : [];
      t12 = {
        label: "Priority",
        value: renderTemplate(mapping.priority, templateContent, group)
      };
      if ($[22] !== mapping.displayName) {
        t13 = {
          label: "Document type",
          value: mapping.displayName
        };
        $[22] = mapping.displayName;
        $[23] = t13;
      } else {
        t13 = $[23];
      }
      t14 = {
        label: "Document label",
        value: renderTemplate(mapping.documentLabel, templateContent, group)
      };
      if ($[24] !== mapping.inputDate) {
        t15 = {
          label: "Input date",
          value: mapping.inputDate
        };
        $[24] = mapping.inputDate;
        $[25] = t15;
      } else {
        t15 = $[25];
      }
      t9 = isHeartplaceNonMrr ? "Folder" : "Upload to folder";
      t10 = renderTemplate(mapping.folder || "", templateContent, group);
      $[4] = authLocation;
      $[5] = mapping.displayName;
      $[6] = mapping.documentLabel;
      $[7] = mapping.folder;
      $[8] = mapping.group;
      $[9] = mapping.inputDate;
      $[10] = mapping.priority;
      $[11] = mapping.workflowType;
      $[12] = reviewedContent;
      $[13] = isHeartplaceNonMrr;
      $[14] = processInstructionText;
      $[15] = t10;
      $[16] = t11;
      $[17] = t12;
      $[18] = t13;
      $[19] = t14;
      $[20] = t15;
      $[21] = t9;
    } else {
      isHeartplaceNonMrr = $[13];
      processInstructionText = $[14];
      t10 = $[15];
      t11 = $[16];
      t12 = $[17];
      t13 = $[18];
      t14 = $[19];
      t15 = $[20];
      t9 = $[21];
    }
    let t16;
    if ($[26] !== t10 || $[27] !== t9) {
      t16 = {
        label: t9,
        value: t10
      };
      $[26] = t10;
      $[27] = t9;
      $[28] = t16;
    } else {
      t16 = $[28];
    }
    let t17;
    if ($[29] !== authLocation || $[30] !== locationPrefix || $[31] !== mapping.assignedTo || $[32] !== resolvedAssignment) {
      t17 = resolvedAssignment ?? (authLocation && isPassthroughAssignee(authLocation) && /\{\{?location\}\}?/.test(mapping.assignedTo ?? "") ? authLocation : replaceLocationPlaceholder(mapping.assignedTo?.replace(/\{\{?authPrefix\}\}?/g, locationPrefix ?? "HP") ?? null, authLocation));
      $[29] = authLocation;
      $[30] = locationPrefix;
      $[31] = mapping.assignedTo;
      $[32] = resolvedAssignment;
      $[33] = t17;
    } else {
      t17 = $[33];
    }
    let t18;
    if ($[34] !== t17) {
      t18 = {
        label: "Assign to",
        value: t17
      };
      $[34] = t17;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    let standardFields;
    if ($[36] !== mapping.markAsReviewed || $[37] !== t11 || $[38] !== t12 || $[39] !== t13 || $[40] !== t14 || $[41] !== t15 || $[42] !== t16 || $[43] !== t18) {
      standardFields = [...t11, t12, t13, t14, t15, t16, t18].filter(_temp);
      if (mapping.markAsReviewed != null) {
        const t19 = mapping.markAsReviewed ? "Yes" : "No";
        let t20;
        if ($[45] !== t19) {
          t20 = {
            label: "Mark as reviewed",
            value: t19
          };
          $[45] = t19;
          $[46] = t20;
        } else {
          t20 = $[46];
        }
        standardFields.push(t20);
      }
      $[36] = mapping.markAsReviewed;
      $[37] = t11;
      $[38] = t12;
      $[39] = t13;
      $[40] = t14;
      $[41] = t15;
      $[42] = t16;
      $[43] = t18;
      $[44] = standardFields;
    } else {
      standardFields = $[44];
    }
    let extraFields;
    if ($[47] !== ehrDocumentId || $[48] !== isHeartplaceNonMrr || $[49] !== locationPrefix || $[50] !== mapping.group || $[51] !== mapping.teMessage || $[52] !== patientEhrSystemId || $[53] !== patientFamilyName || $[54] !== patientGivenName || $[55] !== staffLocation) {
      extraFields = [...getGroupExtraFields(mapping.group, patientFamilyName, patientGivenName, patientEhrSystemId, ehrDocumentId)];
      if (staffLocation) {
        const staffLocationValue = isHeartplaceNonMrr && locationPrefix ? `${locationPrefix} ${staffLocation}` : staffLocation;
        const t19 = isHeartplaceNonMrr ? "Fax sent to" : "Staff Location";
        let t20;
        if ($[57] !== staffLocationValue || $[58] !== t19) {
          t20 = {
            label: t19,
            value: staffLocationValue
          };
          $[57] = staffLocationValue;
          $[58] = t19;
          $[59] = t20;
        } else {
          t20 = $[59];
        }
        extraFields.push(t20);
      }
      if (mapping.teMessage) {
        let t19;
        if ($[60] !== mapping.teMessage) {
          t19 = {
            label: "TE message",
            value: mapping.teMessage
          };
          $[60] = mapping.teMessage;
          $[61] = t19;
        } else {
          t19 = $[61];
        }
        extraFields.push(t19);
      }
      $[47] = ehrDocumentId;
      $[48] = isHeartplaceNonMrr;
      $[49] = locationPrefix;
      $[50] = mapping.group;
      $[51] = mapping.teMessage;
      $[52] = patientEhrSystemId;
      $[53] = patientFamilyName;
      $[54] = patientGivenName;
      $[55] = staffLocation;
      $[56] = extraFields;
    } else {
      extraFields = $[56];
    }
    const t19 = mapping.note ?? null;
    const t20 = mapping.noteLabel ?? null;
    const t21 = mapping.statusLine ?? null;
    const t22 = mapping.workflowAction ?? null;
    const t23 = mapping.tags ?? null;
    const t24 = stepOutputs ?? null;
    const t25 = ccDescription ?? null;
    let t26;
    if ($[62] !== extraFields || $[63] !== faxRecipientNumber || $[64] !== mapping.additionalWorkflow || $[65] !== mapping.displayName || $[66] !== mapping.instructions || $[67] !== processInstructionText || $[68] !== standardFields || $[69] !== t19 || $[70] !== t20 || $[71] !== t21 || $[72] !== t22 || $[73] !== t23 || $[74] !== t24 || $[75] !== t25) {
      t26 = {
        displayName: mapping.displayName,
        standardFields,
        extraFields,
        instructions: mapping.instructions,
        note: t19,
        noteLabel: t20,
        statusLine: t21,
        workflowAction: t22,
        additionalWorkflow: mapping.additionalWorkflow,
        faxRecipientNumber,
        tags: t23,
        stepOutputs: t24,
        ccDescription: t25,
        processInstructionText
      };
      $[62] = extraFields;
      $[63] = faxRecipientNumber;
      $[64] = mapping.additionalWorkflow;
      $[65] = mapping.displayName;
      $[66] = mapping.instructions;
      $[67] = processInstructionText;
      $[68] = standardFields;
      $[69] = t19;
      $[70] = t20;
      $[71] = t21;
      $[72] = t22;
      $[73] = t23;
      $[74] = t24;
      $[75] = t25;
      $[76] = t26;
    } else {
      t26 = $[76];
    }
    t8 = t26;
  }
  return t8;
}
function _temp(field) {
  return field.value;
}
export function useProcessedInstructions(task, section, t0) {
  const $ = _c(14);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== enabled) {
    t1 = {
      enabled
    };
    $[0] = enabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    instructions,
    stepOutputs,
    ccDescription,
    isLoading: docLoading,
    isError: docError
  } = useDocumentManagementInstructions(task.id, t1);
  let t2;
  if ($[2] !== enabled) {
    t2 = {
      enabled
    };
    $[2] = enabled;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const {
    authLocation,
    staffLocation,
    faxRecipientNumber,
    locationPrefix,
    resolvedAssignment,
    isLoading: locLoading
  } = useFaxLocationAssignment(task.id, t2);
  let t3;
  if ($[4] !== authLocation || $[5] !== faxRecipientNumber || $[6] !== locationPrefix || $[7] !== resolvedAssignment || $[8] !== staffLocation) {
    t3 = {
      authLocation,
      staffLocation,
      locationPrefix,
      faxRecipientNumber,
      resolvedAssignment
    };
    $[4] = authLocation;
    $[5] = faxRecipientNumber;
    $[6] = locationPrefix;
    $[7] = resolvedAssignment;
    $[8] = staffLocation;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const data = useBuildInstructions(task, section, instructions, stepOutputs, ccDescription, t3);
  const t4 = docLoading || locLoading;
  let t5;
  if ($[10] !== data || $[11] !== docError || $[12] !== t4) {
    t5 = {
      isLoading: t4,
      isError: docError,
      data
    };
    $[10] = data;
    $[11] = docError;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}